"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Globe, Menu, X, ShoppingCart, DollarSign } from 'lucide-react';
import { usePreferences } from '@/contexts/preferences-context';
import { trackEvent } from '@/lib/analytics/trackEvent';
import { getItemCount, subscribeCart } from '@/lib/cart/cart';

const HotzyLogo = () => (
  <div className="flex items-center gap-3">
    <div className="relative h-10 w-10 overflow-hidden rounded-xl border border-primary/30 bg-black/60 shadow-[0_0_18px_rgba(118,185,0,0.2)]">
      <Image
        src="/branding/hotzy-logo.png"
        alt="Hotzy logo"
        fill
        sizes="40px"
        className="object-cover"
        priority
      />
    </div>
    <span className="text-xl font-black tracking-[0.2em] text-primary">HOTZY</span>
  </div>
);

const NavigationHeader = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { currency, setCurrency, language, setLanguage } = usePreferences();
    const [cartItemCount, setCartItemCount] = useState(0);
    const router = useRouter();

    useEffect(() => {
        const updateCount = () => {
            setCartItemCount(getItemCount());
        };

        updateCount();
        const unsubscribe = subscribeCart(updateCount);
        return () => {
            unsubscribe();
        };
    }, []);

    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? 'hidden' : 'unset';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMenuOpen]);

    const mainNavItems = [
        { name: "Collections", href: "/#design-gallery" },
        { name: "Customizer", href: "/customizer" },
        { name: "Gallery", href: "/#gallery" },
        { name: "Discover", href: "/discover" }
    ];
    const utilityNavItems = [
        { name: "Shop", href: "/shop" }
    ];

    const handleCartClick = () => {
        router.push('/checkout');
    };

    return (
        <>
            <header className="sticky top-0 z-[1000] w-full bg-black/95 backdrop-blur-sm h-16 text-white border-b border-border">
                <div className="container flex h-full items-center justify-between">
                    <div className="flex items-center gap-x-8">
                        <Link href="/" aria-label="Hotzy Homepage">
                            <HotzyLogo />
                        </Link>
                        <nav className="hidden lg:flex">
                            <ul className="flex items-center gap-x-6">
                                {mainNavItems.map((item) => (
                                    <li key={item.name}>
                                        <Link href={item.href} className="text-sm font-medium hover:text-primary transition-colors">{item.name}</Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>

                    <div className="flex items-center">
                        <nav className="hidden lg:flex items-center">
                            <ul className="flex items-center gap-x-6">
                                {utilityNavItems.map((item) => (
                                    <li key={item.name}>
                                        <Link href={item.href} className="text-sm font-medium hover:text-primary transition-colors">{item.name}</Link>
                                    </li>
                                ))}
                            </ul>
                            <div className="flex items-center gap-x-4 ml-6">
                                <button
                                    type="button"
                                    onClick={handleCartClick}
                                    aria-label="Shopping cart"
                                    className="relative z-50 pointer-events-auto hover:text-primary transition-colors"
                                >
                                    <ShoppingCart size={20} />
                                    {cartItemCount > 0 && (
                                        <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                            {cartItemCount}
                                        </span>
                                    )}
                                </button>
                                <button 
                                    onClick={() => {
                                        const next = currency === 'CAD' ? 'USD' : 'CAD';
                                        setCurrency(next);
                                        void trackEvent('filter_change', {
                                            page_context: 'home',
                                            filter_name: 'currency',
                                            filter_value: next,
                                            filter_action: 'set',
                                            ui_component: 'header_toggle',
                                        });
                                    }}
                                    aria-label="Toggle currency" 
                                    className="hover:text-primary transition-colors flex items-center gap-1">
                                    <DollarSign size={18} />
                                    <span className="text-xs font-bold">{currency}</span>
                                </button>
                                <button 
                                    onClick={() => {
                                        const next = language === 'en' ? 'fr' : 'en';
                                        setLanguage(next);
                                        void trackEvent('filter_change', {
                                            page_context: 'home',
                                            filter_name: 'language',
                                            filter_value: next,
                                            filter_action: 'set',
                                            ui_component: 'header_toggle',
                                        });
                                    }}
                                    aria-label="Toggle language" 
                                    className="hover:text-primary transition-colors flex items-center gap-1">
                                    <Globe size={18} />
                                    <span className="text-xs font-bold">{language.toUpperCase()}</span>
                                </button>
                            </div>
                        </nav>

                        <div className="flex items-center gap-x-4 lg:hidden">
                            <button
                                type="button"
                                onClick={handleCartClick}
                                aria-label="Shopping cart"
                                className="relative z-50 pointer-events-auto hover:text-primary transition-colors"
                            >
                                <ShoppingCart size={22} />
                                {cartItemCount > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                        {cartItemCount}
                                    </span>
                                )}
                            </button>
                            <button 
                                onClick={() => {
                                    const next = currency === 'CAD' ? 'USD' : 'CAD';
                                    setCurrency(next);
                                    void trackEvent('filter_change', {
                                        page_context: 'home',
                                        filter_name: 'currency',
                                        filter_value: next,
                                        filter_action: 'set',
                                        ui_component: 'header_toggle',
                                    });
                                }}
                                aria-label="Toggle currency" 
                                className="hover:text-primary transition-colors flex items-center gap-1">
                                <DollarSign size={20} />
                                <span className="text-xs font-bold">{currency}</span>
                            </button>
                            <button 
                                onClick={() => {
                                    const next = language === 'en' ? 'fr' : 'en';
                                    setLanguage(next);
                                    void trackEvent('filter_change', {
                                        page_context: 'home',
                                        filter_name: 'language',
                                        filter_value: next,
                                        filter_action: 'set',
                                        ui_component: 'header_toggle',
                                    });
                                }}
                                aria-label="Toggle language" 
                                className="hover:text-primary transition-colors flex items-center gap-1">
                                <Globe size={20} />
                                <span className="text-xs font-bold">{language.toUpperCase()}</span>
                            </button>
                            <button onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" className="z-[1001]">
                                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {isMenuOpen && (
                <div className="lg:hidden fixed inset-0 top-16 bg-black z-[999] overflow-y-auto">
                    <div className="container py-6">
                        <nav className="flex flex-col">
                            {mainNavItems.map((item) => (
                                <Link href={item.href} key={item.name} className="py-3 text-lg font-medium text-white hover:text-primary">{item.name}</Link>
                            ))}
                            <div className="border-t border-border my-4" />
                            {utilityNavItems.map((item) => (
                                <Link href={item.href} key={item.name} className="py-3 text-lg text-white hover:text-primary">{item.name}</Link>
                            ))}
                        </nav>
                    </div>
                </div>
            )}
        </>
    );
};

export default NavigationHeader;
